/* eslint-disable eqeqeq */
import { useEffect, useMemo } from 'react';
import { useAppDispatch } from 'app/hooks';
import {
  ahuBoardApi,
  useGetSectionHorizontalIntWallsQuery,
  useGetSectionVerticalIntWallsQuery,
} from 'app/services/ahuBoardApi';
import { useGetIntWallPlanesQuery } from 'app/services/ahuBoardFormsApi';
import { ahuBoardBus } from '../../bus/ahuBoardBus';

/**
 * Phase 6e2b-2 — THE shared IW plane-resolution dataGetter (§51.2 damper row;
 * built here per §53.3 order step 4 so damper/access_door/opening reuse the
 * louver-grade machinery):
 *
 *  - allowed_mps = cms_accessory.mounting_plane entries matching
 *    /^internal_wall_[a-z]+/ whose plane suffix is ALSO in
 *    fn['cms.cabinet.iw_planes'] (GET cms/1/v1.0.0/ahu/section_internal_wall/
 *    planes/ — the CMS > Construction > Cabinet IW selection);
 *  - ALLOWED_IW_PLANES = the suffixes ('vertical'/'horizontal');
 *  - IW_NAME_MAPPING[suffix] = mp.name → option text `<IW name> : <tag>`;
 *  - section IW list = vertical + horizontal collections merged, sorted by id,
 *    name-mapped, plane-filtered (accessories/damper/view/form.js:51-64);
 *  - `mounting_planes_on_cabinet` = cms list with internal_wall_* STRIPPED
 *    (the on-cabinet select half — §49.6 render / parity 330);
 *  - internal_wall:created/deleted/change → refetch both collections (the
 *    damper/access_door/opening dispatcher listeners — §51.2).
 */

export interface InternalWallOption extends Record<string, unknown> {
  id: number;
  name: string;
  plane?: string;
  tag?: string;
}

export interface InternalWallsData {
  /** sorted/named/filtered IW rows (dataGetter `internal_walls`). */
  internalWalls: InternalWallOption[] | undefined;
  /** cms mounting planes with internal_wall_* stripped. */
  mountingPlanesOnCabinet: Array<Record<string, unknown>>;
}

export function useSectionInternalWalls(
  sectionId: number,
  cmsAccessory: Record<string, unknown> | undefined,
  enabled = true
): InternalWallsData {
  const dispatch = useAppDispatch();
  const { data: vertical, refetch: refetchV } = useGetSectionVerticalIntWallsQuery(sectionId, {
    skip: !enabled,
  });
  const { data: horizontal, refetch: refetchH } = useGetSectionHorizontalIntWallsQuery(sectionId, {
    skip: !enabled,
  });
  const { data: cabinetPlanes } = useGetIntWallPlanesQuery(undefined, { skip: !enabled });

  // internal_wall:created/deleted/change → render (refetch both collections).
  useEffect(() => {
    if (!enabled) return undefined;
    const offs = (['internal_wall:created', 'internal_wall:deleted', 'internal_wall:change'] as const).map(
      (evt) =>
        ahuBoardBus.on(evt, () => {
          void refetchV();
          void refetchH();
          // canvas-side IW caches refresh through their own listeners; the
          // RTKQ invalidation keeps the select options current.
          void dispatch(
            ahuBoardApi.util.invalidateTags([{ type: 'AhuSections', id: `viw-${sectionId}` }])
          );
        })
    );
    return () => offs.forEach((off) => off());
  }, [enabled, refetchV, refetchH, dispatch, sectionId]);

  return useMemo(() => {
    const planes =
      ((cmsAccessory?.mounting_plane as Array<Record<string, unknown>>) ?? []).filter(
        (p) => typeof p.value === 'string'
      );
    const mountingPlanesOnCabinet = planes.filter(
      (p) => !/^internal_wall_[a-z]+/.test(String(p.value))
    );
    if (!enabled) {
      return { internalWalls: undefined, mountingPlanesOnCabinet };
    }
    const allowedMps = planes.filter(
      (p) =>
        /^internal_wall_[a-z]+/.test(String(p.value)) &&
        ((cabinetPlanes as Array<Record<string, unknown>> | undefined) ?? []).some(
          (row) => String(row.value) == String(p.value).split('_')[2]
        )
    );
    const allowedPlaneNames = allowedMps.map((p) => String(p.value).split('_')[2]);
    const nameMapping: Record<string, string> = {};
    allowedMps.forEach((p) => {
      nameMapping[String(p.value).split('_')[2]] = String(p.name ?? '');
    });
    const merged = [...(vertical ?? []), ...(horizontal ?? [])] as Array<Record<string, unknown>>;
    const internalWalls =
      merged.length === 0
        ? undefined // legacy: no IW collections → internal_walls stays undefined
        : (merged
            .slice()
            .sort((a, b) => Number(a.id) - Number(b.id))
            .map(
              (el) =>
                ({
                  ...el,
                  name:
                    `${nameMapping[String(el.plane)] ?? ''}` +
                    (el.tag ? ` : ${String(el.tag)}` : ''),
                }) as InternalWallOption
            )
            .filter((iw) => allowedPlaneNames.indexOf(String(iw.plane)) !== -1) as InternalWallOption[]);
    return { internalWalls, mountingPlanesOnCabinet };
  }, [cmsAccessory, vertical, horizontal, cabinetPlanes, enabled]);
}
